/**
 * Invoice PDF Service
 *
 * Generates, stores, retrieves, views, and downloads invoice PDFs.
 * Uses @react-pdf/renderer for client-side PDF generation from
 * billing Invoice data.
 *
 * Storage: PDFs are persisted in Azure Table Storage via the backend
 * API endpoint POST /api/billing/invoices/{id}/pdf.
 */

import { Buffer } from 'buffer';
if (typeof globalThis.Buffer === 'undefined') {
  globalThis.Buffer = Buffer;
}

import { pdf } from '@react-pdf/renderer';
import type { ReactElement } from 'react';
import { createElement } from 'react';
import type { DocumentProps } from '@react-pdf/renderer';
import { InvoicePdfTemplate } from '@/components/invoices/InvoicePdfTemplate';
import { authFetch } from '@/lib/auth-fetch';
import { getApiBaseUrl } from '@/lib/data-service';
import type {
  InvoicePdfData,
  InvoicePdfTransaction,
  InvoicePdfResult,
  InvoicePdfLineItem,
  CompanyDetails,
  StoredInvoicePdf,
} from '@/types/invoice-pdf';
import type { BillingInvoiceLineItem } from '@/lib/data-service';
import type { Company } from '@/types/company';
import type { InvoiceTemplate, InvoicePreviewData, InvoiceLineItem as TemplateLineItem } from '@/lib/invoice-template-types';
import { getInvoiceTemplates, getInvoiceTemplate } from '@/services/invoiceTemplateService';
import { getCompanyDetails, setCurrentCompanyId } from '@/services/companyService';
import { convertImageToPngDataUrl, detectPdfSafeImageFormat } from '@/lib/compress-image';

/** Empty company details — used when no company data is available */
const EMPTY_COMPANY_DETAILS: CompanyDetails = {
  name: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postcode: '',
  country: '',
  phone: '',
  email: '',
  vatNumber: '',
  companyNumber: '',
};

const DEFAULT_PAYMENT_TERMS =
  'Payment is due within 30 days of the invoice date. ' +
  'Please quote the invoice number on all remittances. ' +
  'Late payments may be subject to statutory interest charges.';

/**
 * Build CompanyDetails from the Company entity provided by CompanyContext.
 * Falls back to DEFAULT_COMPANY_DETAILS for any missing fields.
 */
export function buildCompanyDetails(company: Company): CompanyDetails {
  const addr = company.registeredAddress;
  return {
    name: company.name || '',
    addressLine1: addr?.line1 || '',
    addressLine2: addr?.line2 || '',
    city: addr?.city || '',
    postcode: addr?.postcode || '',
    country: addr?.country || '',
    phone: company.phone || '',
    email: company.email || '',
    vatNumber: company.vatNumber || '',
    companyNumber: company.companyRegistrationNumber || '',
    logoUrl: company.logoUrl,
    bankDetails: company.bankDetails,
  };
}

/**
 * Fetch full company details from the detail endpoint and map to the
 * invoice-pdf CompanyDetails shape. Falls back to buildCompanyDetails()
 * if the detail fetch fails or returns empty data.
 *
 * This is the preferred way to obtain company details for PDF generation
 * because the list endpoint (used by CompanyContext) does not return
 * invoicing-specific fields like registered address, VAT number, etc.
 */
export async function fetchFullCompanyDetails(
  company: Company | null,
): Promise<CompanyDetails | undefined> {
  if (!company) return undefined;
  try {
    setCurrentCompanyId(company.id);
    const fullDetails = await getCompanyDetails();
    if (fullDetails.name) {
      return {
        name: fullDetails.name,
        addressLine1: fullDetails.registeredAddress?.line1 || '',
        addressLine2: fullDetails.registeredAddress?.line2 || '',
        city: fullDetails.registeredAddress?.city || '',
        postcode: fullDetails.registeredAddress?.postcode || '',
        country: fullDetails.registeredAddress?.country || '',
        phone: fullDetails.phone || '',
        email: fullDetails.email || '',
        vatNumber: fullDetails.vatNumber || '',
        companyNumber: fullDetails.companyRegistrationNumber || '',
        logoUrl: fullDetails.logoUrl,
        bankDetails: fullDetails.bankDetails,
      };
    }
  } catch {
    // Fallback below
  }
  return buildCompanyDetails(company);
}

/**
 * Convert backend BillingInvoiceLineItems to the PDF line-item shape.
 */
function mapBillingLineItems(items: BillingInvoiceLineItem[]): InvoicePdfLineItem[] {
  return items.map((item) => ({
    description: item.description || 'Professional services',
    code: '',
    quantity: item.quantity,
    unitPrice: item.rate,
    value: item.netAmount,
    taxAmount: item.taxAmount,
    grossAmount: item.grossAmount,
  }));
}

/**
 * Fallback: build a single synthetic line item when no real line items are available.
 */
function buildFallbackLineItems(transaction: InvoicePdfTransaction): InvoicePdfLineItem[] {
  return [
    {
      description: transaction.description || 'Professional services',
      code: 'SRV-GEN',
      quantity: 1,
      unitPrice: transaction.amount,
      value: transaction.amount,
    },
  ];
}

function bytesToBase64(bytes: Uint8Array): string {
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

/**
 * Convert an image blob to a data URL @react-pdf/renderer can decode.
 *
 * The renderer only understands PNG, JPEG and SVG, and it identifies the
 * format from the data URL's MIME type. Anything else (WebP, GIF, AVIF…) —
 * or a blob whose declared MIME type contradicts its bytes — is rasterised
 * to PNG first, otherwise the renderer discards the image with nothing more
 * than a console warning and the invoice is produced without its logo.
 */
async function blobToPdfSafeDataUrl(blob: Blob): Promise<string> {
  const bytes = new Uint8Array(await blob.arrayBuffer());
  const format = detectPdfSafeImageFormat(bytes, blob.type);
  if (!format) return convertImageToPngDataUrl(blob);
  const mimeType = format === 'svg' ? 'image/svg+xml' : `image/${format}`;
  return `data:${mimeType};base64,${bytesToBase64(bytes)}`;
}

/**
 * Pre-fetch an image URL with authentication and convert to a base64 data URL.
 * @react-pdf/renderer fetches images with its own HTTP client which does not
 * include browser cookies or auth headers, so authenticated proxy URLs (e.g.
 * /invoice-templates/logo-image?path=…) fail silently. Converting to a data
 * URL beforehand ensures the image renders in the PDF.
 */
async function fetchImageAsDataUrl(url: string): Promise<string> {
  if (!url) return url;
  try {
    if (url.startsWith('data:')) {
      const declaredMime = url.slice('data:'.length, url.indexOf(';')).toLowerCase();
      if (declaredMime === 'image/png' || declaredMime === 'image/jpeg' || declaredMime === 'image/svg+xml') {
        return url;
      }
      const response = await fetch(url);
      return await blobToPdfSafeDataUrl(await response.blob());
    }

    const response = await authFetch(url, { credentials: 'include' });
    if (!response.ok) return url;
    return await blobToPdfSafeDataUrl(await response.blob());
  } catch (err) {
    console.warn('[InvoicePdf] Could not prepare image for the PDF; it may not render', err);
    return url;
  }
}

/**
 * Generate a PDF for the given billing invoice.
 *
 * @param billingLineItems — actual line items from the backend. When provided,
 *   these are used instead of a synthetic fallback.
 */
export async function generateInvoicePdf(
  transaction: InvoicePdfTransaction,
  companyDetails: CompanyDetails = EMPTY_COMPANY_DETAILS,
  billingLineItems?: BillingInvoiceLineItem[],
  template?: InvoiceTemplate,
  previewData?: InvoicePreviewData,
  notes?: string,
): Promise<InvoicePdfResult> {
  const lineItems = billingLineItems && billingLineItems.length > 0
    ? mapBillingLineItems(billingLineItems)
    : buildFallbackLineItems(transaction);

  // Pre-fetch logo images with auth so @react-pdf/renderer can render them
  let resolvedTemplate = template;
  let resolvedPreviewData = previewData;

  if (template?.branding?.logo) {
    const logoDataUrl = await fetchImageAsDataUrl(template.branding.logo);
    if (logoDataUrl !== template.branding.logo) {
      resolvedTemplate = {
        ...template,
        branding: { ...template.branding, logo: logoDataUrl },
      };
    }
  }
  if (previewData?.company?.logo) {
    const companyLogoDataUrl = await fetchImageAsDataUrl(previewData.company.logo);
    if (companyLogoDataUrl !== previewData.company.logo) {
      resolvedPreviewData = {
        ...previewData,
        company: { ...previewData.company, logo: companyLogoDataUrl },
      };
    }
  }

  const pdfData: InvoicePdfData = {
    transaction,
    companyDetails,
    lineItems,
    paymentTerms: DEFAULT_PAYMENT_TERMS,
    notes,
    template: resolvedTemplate,
    previewData: resolvedPreviewData,
  };

  const element = createElement(InvoicePdfTemplate, { data: pdfData }) as ReactElement<DocumentProps>;
  const pdfBlob = await pdf(element).toBlob();

  return {
    invoiceKey: transaction.invoiceNumber,
    invoiceNumber: transaction.invoiceNumber,
    pdfBlob,
    generatedAt: new Date(),
  };
}

// ── Helper: convert Blob → Base64 string ──────────────────────────────────
export async function blobToBase64(blob: Blob): Promise<string> {
  const buffer = await blob.arrayBuffer();
  const bytes = new Uint8Array(buffer);
  let binary = '';
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return btoa(binary);
}

export function base64ToBlob(base64: string, contentType = 'application/pdf'): Blob {
  const byteCharacters = atob(base64);
  const byteNumbers = new Array(byteCharacters.length);
  for (let i = 0; i < byteCharacters.length; i++) {
    byteNumbers[i] = byteCharacters.charCodeAt(i);
  }
  return new Blob([new Uint8Array(byteNumbers)], { type: contentType });
}

/**
 * Save a generated PDF to backend table storage.
 *
 * Calls POST /api/billing/invoices/{id}/pdf with the PDF as Base64.
 * The invoiceId must be a numeric ID returned from the backend.
 */
export async function saveInvoicePdf(
  result: InvoicePdfResult,
  invoiceId?: number | string,
): Promise<void> {
  const id = invoiceId ?? result.invoiceKey;
  const pdfBase64 = await blobToBase64(result.pdfBlob);

  const response = await authFetch(`${getApiBaseUrl()}/billing/invoices/${id}/pdf`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ pdfBase64 }),
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(
      (errorData as { error?: string }).error ??
        `Failed to save PDF: ${response.status} ${response.statusText}`,
    );
  }
}

/**
 * Retrieve a stored PDF by invoice ID from the backend.
 *
 * Calls GET /api/billing/invoices/{id}/pdf which returns JSON with base64-encoded
 * PDF data. The base64 is decoded client-side into a Blob.
 */
export async function getStoredInvoicePdf(
  invoiceKey: string,
): Promise<StoredInvoicePdf | null> {
  try {
    const response = await authFetch(`${getApiBaseUrl()}/billing/invoices/${invoiceKey}/document`, {
      credentials: 'include',
    });

    if (!response.ok) return null;

    const data: {
      pdfBase64?: string;
      contentType?: string;
      invoiceNumber?: string;
      sizeBytes?: number;
    } = await response.json();

    if (!data.pdfBase64) return null;

    const pdfBlob = base64ToBlob(data.pdfBase64, data.contentType ?? 'application/pdf');

    return {
      invoiceKey,
      invoiceNumber: data.invoiceNumber ?? invoiceKey,
      pdfBlob,
      contentType: data.contentType ?? 'application/pdf',
      generatedAt: new Date(),
      sizeBytes: data.sizeBytes ?? pdfBlob.size,
    };
  } catch {
    return null;
  }
}

/**
 * Check whether a PDF has already been generated for this invoice.
 *
 * Calls GET /api/billing/invoices/{id}/pdf/exists
 */
export async function hasStoredPdf(invoiceKey: string): Promise<boolean> {
  try {
    const response = await authFetch(
      `${getApiBaseUrl()}/billing/invoices/${invoiceKey}/pdf/exists`,
      { credentials: 'include' },
    );
    if (!response.ok) return false;
    const data: { exists?: boolean } = await response.json();
    return data.exists === true;
  } catch {
    return false;
  }
}

/**
 * Check which of the given invoices already have a stored PDF.
 *
 * Calls POST /api/billing/invoices/pdf/exists-batch with all IDs in one
 * request, replacing N individual /exists calls with a single round-trip.
 */
export async function hasStoredPdfBatch(
  invoiceIds: (number | string)[],
): Promise<Set<string>> {
  if (invoiceIds.length === 0) return new Set();
  try {
    const response = await authFetch(
      `${getApiBaseUrl()}/billing/invoices/pdf/exists-batch`,
      {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ invoiceIds: invoiceIds.map(Number) }),
      },
    );
    if (!response.ok) return new Set();
    const data: { invoiceIds?: number[] } = await response.json();
    return new Set((data.invoiceIds ?? []).map(String));
  } catch {
    return new Set();
  }
}

/**
 * Fetch the default invoice template directly from the API.
 * This is used instead of relying on React context which may not be loaded yet.
 * If a specific templateId is provided, that template is fetched; otherwise the
 * default (or first available) template is returned.
 */
export async function fetchDefaultInvoiceTemplate(
  templateId?: string | number | null,
): Promise<InvoiceTemplate | undefined> {
  try {
    if (templateId != null) {
      const tpl = await getInvoiceTemplate(templateId);
      if (tpl) return tpl;
    }
    const all = await getInvoiceTemplates();
    return all.find(t => t.isDefault) || all[0];
  } catch {
    return undefined;
  }
}

/**
 * Build InvoicePreviewData from a transaction, company details, and billing line items.
 * This is the data structure expected by the template-driven PDF renderer.
 */
function formatDateForPreview(dateStr: string): string {
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}/${month}/${year}`;
}

/**
 * The `net` field of a template line item feeds the table's "Rate" column, so it
 * carries the per-unit bill rate rather than the line's net total.
 *
 * `BillingInvoiceLineItem.rate` is a required `number` with no null sentinel, and
 * lines written before the rate was persisted store `0`. A rate of `0` is therefore
 * only meaningful on a line whose net is also `0` (`0 x quantity = 0`); when it sits
 * on a line with a net value it cannot be the rate that produced that net, so the
 * rate is derived from the line instead. Every other stored rate, `0` on a zero-value
 * line included, is returned as-is.
 */
function resolveUnitRate(item: BillingInvoiceLineItem): number {
  if (item.rate !== 0) return item.rate;
  if (item.netAmount === 0 || item.quantity === 0) return 0;
  return item.netAmount / item.quantity;
}

export function buildPreviewDataForPdf(
  transaction: InvoicePdfTransaction,
  companyDetails: CompanyDetails,
  billingLineItems?: BillingInvoiceLineItem[],
  customerVatCode?: string,
  customerVatRate?: number,
): InvoicePreviewData {
  const addressParts = [
    companyDetails.addressLine1,
    companyDetails.addressLine2,
    [companyDetails.city, companyDetails.postcode].filter(Boolean).join(', '),
    companyDetails.country,
  ].filter(Boolean);

  const vatRate = (customerVatRate ?? 20) / 100;
  const vatCode = customerVatCode ?? 'S';

  const lineItems: TemplateLineItem[] = billingLineItems && billingLineItems.length > 0
    ? billingLineItems.map((item, idx) => ({
        id: `line-${idx}`,
        date: item.wipDate ? formatDateForPreview(item.wipDate) : '',
        description: item.description || 'Professional services',
        costCentreCode: item.wipCostCentreCode || '',
        costCentreName: item.wipCostCentreName || '',
        person: item.wipResourceName || '',
        role: item.wipRoleName || '',
        phase: item.wipPhase || '',
        task: item.wipTask || '',
        qty: item.quantity,
        net: resolveUnitRate(item),
        vat: item.taxAmount,
        vatCode: item.taxAmount > 0 ? vatCode : '',
        amount: item.netAmount,
        typeLabel: item.type,
      }))
    : [{
        id: 'line-0',
        date: '',
        description: transaction.description || 'Professional services',
        costCentreCode: '',
        costCentreName: '',
        person: '',
        role: '',
        phase: '',
        task: '',
        qty: 1,
        net: transaction.amount,
        vat: transaction.amount * vatRate,
        vatCode,
        amount: transaction.amount,
      }];

  const subtotal = billingLineItems && billingLineItems.length > 0
    ? billingLineItems.reduce((sum, item) => sum + item.netAmount, 0)
    : transaction.amount;
  const tax = billingLineItems && billingLineItems.length > 0
    ? billingLineItems.reduce((sum, item) => sum + item.taxAmount, 0)
    : subtotal * vatRate;

  return {
    company: {
      name: companyDetails.name,
      address: addressParts.join('\n'),
      phone: companyDetails.phone || '',
      email: companyDetails.email || '',
      logo: companyDetails.logoUrl,
      vatNumber: companyDetails.vatNumber,
      registrationNumber: companyDetails.companyNumber,
      bankDetails: companyDetails.bankDetails,
    },
    invoice: {
      number: transaction.invoiceNumber,
      date: formatDateForPreview(transaction.date),
      dueDate: transaction.dueDate ? formatDateForPreview(transaction.dueDate) : '',
    },
    client: {
      name: transaction.accountName || 'Client',
      address: transaction.accountAddress || '',
      contact: '',
    },
    project: {
      name: transaction.projectName || '',
      description: transaction.projectDescription || '',
      projectManager: transaction.projectManager || '',
    },
    billingPeriod: { start: '', end: '' },
    lineItems,
    totals: {
      subtotal,
      tax,
      discount: 0,
      total: subtotal + tax,
    },
  };
}

/**
 * Generate, save, and return the PDF result in one step.
 * If invoiceId is provided, the PDF will be persisted to backend storage.
 *
 * When template/previewData are not provided, this function fetches the
 * default template from the API and builds preview data automatically.
 *
 * @param billingLineItems — actual line items from the backend.
 */
export async function generateAndStoreInvoicePdf(
  transaction: InvoicePdfTransaction,
  invoiceId?: number | string,
  billingLineItems?: BillingInvoiceLineItem[],
  companyDetails?: CompanyDetails,
  template?: InvoiceTemplate,
  previewData?: InvoicePreviewData,
  templateId?: string | number | null,
  notes?: string,
): Promise<InvoicePdfResult> {
  const resolvedCompany = companyDetails ?? EMPTY_COMPANY_DETAILS;

  let resolvedTemplate = template;
  let resolvedPreviewData = previewData;

  if (!resolvedTemplate) {
    resolvedTemplate = await fetchDefaultInvoiceTemplate(templateId);
  }
  if (resolvedTemplate && !resolvedPreviewData) {
    resolvedPreviewData = buildPreviewDataForPdf(transaction, resolvedCompany, billingLineItems);
  }

  const result = await generateInvoicePdf(transaction, resolvedCompany, billingLineItems, resolvedTemplate, resolvedPreviewData, notes);
  await saveInvoicePdf(result, invoiceId);
  return result;
}

/**
 * Open a PDF blob in a new browser tab for viewing / printing.
 */
export function openPdfInNewTab(pdfBlob: Blob): void {
  const url = URL.createObjectURL(pdfBlob);
  window.open(url, '_blank');
  // Revoke after a delay to allow the tab to load
  setTimeout(() => URL.revokeObjectURL(url), 30000);
}

/**
 * Trigger a download of the PDF blob.
 */
export function downloadPdf(pdfBlob: Blob, filename: string): void {
  const url = URL.createObjectURL(pdfBlob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  setTimeout(() => URL.revokeObjectURL(url), 5000);
}
